/**
 * The Cargo-derived visual fragments for `Dropdown`. (kept in the
 * component verbatim (Hubble byte-for-byte unchanged); this module only
 * describes the look, mirroring `apps/dashboard/src/Cargo/Controls/Dropdown.tsx`:
 * an underline trigger (like the TextInput), a lighter menu, and blue
 * full-row selection with no radio-circle indicators.
 *
 * Colors reference the design tokens (theme.css, light + dark), so the
 * dropdown adapts under `.dark`.
 */

/** Menu surface. Shares `shadow-floating` with every other floating surface
 *  so the whole family reads as one elevation rather than each dropdown
 *  picking its own. The popover portals to `document.body`, so it must sit at
 *  the top floating layer to clear host-app modals (e.g. the dashboard's
 *  legacy Cargo modal at z-index 1000+); `z-[9999]` matches the Tooltip
 *  top-layer convention. (The previous bare `z-200` was a no-op, not a real
 *  Tailwind utility, so the menu fell behind any explicitly-stacked
 *  overlay.) */
export const DROPDOWN_MENU =
  "z-[9999] rounded-md border border-border bg-card shadow-floating";

/** Search box inside the menu. */
export const DROPDOWN_SEARCH_BOX =
  "flex items-center gap-2 rounded-md border border-border px-2 py-1.5 focus-within:border-brand";

/**
 * Trigger underline. 1px resting + `pb-[5px]`; thickens to 2px blue on
 * hover/focus/open and drops to `pb-1` so the trigger height stays put.
 */
export function triggerUnderline(isError: boolean): string {
  return isError
    ? "border-b-2 border-red pb-1"
    : "border-b border-muted-foreground pb-[5px] hover:border-b-2 hover:border-brand hover:pb-1 focus:border-b-2 focus:border-brand focus:pb-1 data-[state=open]:border-b-2 data-[state=open]:border-brand data-[state=open]:pb-1";
}

/** Option row color treatment: blue when selected, subtle-blue hover otherwise. */
export function dropdownRow(isSelected: boolean, isDisabled: boolean): string {
  if (isDisabled) return "cursor-not-allowed text-faint";
  if (isSelected) return "cursor-pointer bg-brand font-medium text-white";
  return "cursor-pointer text-foreground hover:bg-brand-subtle";
}

/** Option description sub-text color (legible on the blue selected row). */
export function dropdownDescription(
  isSelected: boolean,
  isDisabled: boolean,
): string {
  if (isDisabled) return "text-faint";
  if (isSelected) return "text-white";
  return "text-muted-foreground";
}
